import { useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { MapPin, ExternalLink, Share2, Link2 } from "lucide-react";
import { useSiteText, lines, labelLines } from "../lib/siteTexts";

export const FOREST = "#1C2E1F";
export const CREAM = "#F5EFE3";

export function formatTourDate(iso, opts = {}) {
  try {
    return new Date(iso).toLocaleDateString("de-DE", {
      weekday: "long", day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit", ...opts,
    });
  } catch { return iso; }
}

export function TourBadges({ tour, testId }) {
  const t = useSiteText();
  const pill = "inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold";
  const soft = { backgroundColor: "var(--wp-cream)", color: "var(--wp-forest)", border: "1px solid var(--wp-border)" };
  return (
    <div data-testid={testId}>
      <div className="flex flex-wrap gap-1.5">
        {lines(t("tour_badges")).map((b, i) => (
          <span key={`${i}-${b}`} className={pill} style={i === 0 ? { backgroundColor: FOREST, color: CREAM } : soft}>{b}</span>
        ))}
      </div>
      {tour?.season_label && (
        <div className="mt-2 text-sm italic" style={{ color: "var(--wp-olive)" }} data-testid={`${testId}-season`}>{tour.season_label}</div>
      )}
    </div>
  );
}

export function GoodToKnow({ compact = false, testId = "good-to-know" }) {
  const t = useSiteText();
  return (
    <div className={`rounded-2xl ${compact ? "p-4" : "p-6 md:p-8"}`} style={{ backgroundColor: "var(--wp-cream)", border: "1px solid var(--wp-border)" }} data-testid={testId}>
      <h3 className={`font-display ${compact ? "text-xl mb-3" : "text-2xl md:text-3xl mb-5"}`}>{t("tour_good_to_know_title")}</h3>
      <ul className={`space-y-2 ${compact ? "text-xs" : "text-sm md:text-base"}`}>
        {labelLines(t("tour_good_to_know")).map(([k, v], i) => (
          <li key={i} className="leading-relaxed">{k && <b>{k}:</b>} {v}</li>
        ))}
      </ul>
    </div>
  );
}

export function WhatWeDontDo({ testId = "what-we-dont-do" }) {
  const t = useSiteText();
  return (
    <div className="grid md:grid-cols-2 gap-0 rounded-2xl overflow-hidden plant-card" data-testid={testId}>
      <div className="aspect-[4/3] md:aspect-auto">
        <img src={t("tour_dont_do_image")} alt={t("tour_dont_do_image_alt")} loading="lazy" className="w-full h-full object-cover" />
      </div>
      <div className="p-8 md:p-12 flex flex-col justify-center">
        <div className="overline mb-3">{t("tour_dont_do_overline")}</div>
        <h2 className="font-display text-3xl md:text-4xl mb-6">{t("tour_dont_do_title")}</h2>
        <ol className="space-y-4">
          {lines(t("tour_dont_do_items")).map((item, i) => (
            <li key={i} className="flex gap-4 items-baseline">
              <span className="font-display text-2xl shrink-0" style={{ color: "var(--wp-olive)" }}>{String(i + 1).padStart(2, "0")}</span>
              <span className="text-sm md:text-base leading-relaxed">{item}</span>
            </li>
          ))}
        </ol>
        <p className="mt-8 font-display italic text-xl" style={{ color: "var(--wp-olive)" }}>{t("tour_dont_do_closing")}</p>
      </div>
    </div>
  );
}

const EMPTY_FORM = { name: "", email: "", phone: "", participants: 1, message: "" };

export function BookingModal({ date, onClose, onBooked }) {
  const t = useSiteText();
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const seats = (date.max_participants || 15) - (date.current_bookings || 0);
  const input = "w-full px-4 py-3 rounded-xl border";
  const border = { borderColor: "var(--wp-border)" };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(`/tours/${date.id}/book`, { tour_id: date.id, ...form });
      toast.success("Anmeldung erfolgreich! Die Bestätigung mit allen Infos kommt per E-Mail.");
      onBooked?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Anmeldung fehlgeschlagen");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center p-0 sm:p-4" style={{ backgroundColor: "rgba(26,47,28,0.6)" }} onClick={onClose} data-testid="booking-modal">
      <div className="bg-white rounded-t-3xl sm:rounded-2xl max-w-lg w-full p-6 sm:p-8 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display text-2xl mb-1">{date.title}</h3>
        <div className="text-sm opacity-80" data-testid="booking-modal-date">{formatTourDate(date.date)} · Kostenlos</div>
        {date.meeting_point && (
          <div className="text-sm mt-1 flex items-start gap-1.5" data-testid="booking-modal-meeting-point">
            <MapPin className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--wp-olive)" }} /> <span><b>Treffpunkt:</b> {date.meeting_point}</span>
          </div>
        )}
        {!date.meeting_point && <p className="text-xs opacity-70 mt-1">{t("tour_meeting_hint")}</p>}
        <div className="mb-5" />
        <form onSubmit={submit} className="space-y-3">
          <input required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} style={border} data-testid="booking-name" />
          <input required type="email" placeholder="E-Mail" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={input} style={border} data-testid="booking-email" />
          <input placeholder="Telefon (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={input} style={border} data-testid="booking-phone" />
          <input required type="number" min="1" max={Math.max(1, seats)} placeholder="Personen" value={form.participants} onChange={(e) => setForm({ ...form, participants: parseInt(e.target.value) || 1 })} className={input} style={border} data-testid="booking-participants" />
          <textarea placeholder="Nachricht (optional) – z. B. Milchallergie" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={`${input} min-h-[90px]`} style={border} data-testid="booking-message" />
          <GoodToKnow compact testId="booking-good-to-know" />
          <div className="flex gap-3 justify-end pt-1">
            <button type="button" onClick={onClose} className="btn-secondary" data-testid="booking-cancel">Abbrechen</button>
            <button type="submit" disabled={submitting} className="btn-primary" data-testid="booking-submit">
              {submitting ? "Sende…" : "Verbindlich anmelden"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export function DateShare({ d }) {
  const url = `${API_BASE}/api/share/dates/${d.id}`;
  const when = formatTourDate(d.date, { weekday: "short" });
  const text = `${d.title} – ${when}, Treffpunkt: ${d.meeting_point}. Kostenlose Naturwanderung mit AdventureCouple.lu`;
  const canShare = typeof navigator !== "undefined" && !!navigator.share;
  const icon = "inline-flex items-center justify-center w-9 h-9 rounded-full border transition-colors hover:bg-[var(--wp-cream)]";
  const bc = { borderColor: "var(--wp-border)" };
  return (
    <div className="flex items-center gap-1.5" data-testid={`date-share-${d.id}`}>
      <a href={`https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`} target="_blank" rel="noopener noreferrer" className={icon} style={bc} title="Termin per WhatsApp teilen" aria-label="Termin per WhatsApp teilen" data-testid={`date-share-whatsapp-${d.id}`}>
        <i className="fa-brands fa-whatsapp text-base" style={{ color: "#25D366" }} />
      </a>
      {canShare ? (
        <button type="button" onClick={() => navigator.share({ title: d.title, text, url }).catch(() => {})} className={icon} style={bc} title="Termin teilen (Instagram & mehr)" aria-label="Termin teilen" data-testid={`date-share-native-${d.id}`}>
          <Share2 className="w-4 h-4" />
        </button>
      ) : (
        <button type="button" onClick={() => navigator.clipboard?.writeText(url).then(() => toast.success("Termin-Link kopiert"))} className={icon} style={bc} title="Termin-Link kopieren" aria-label="Termin-Link kopieren" data-testid={`date-share-copy-${d.id}`}>
          <Link2 className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

export function TourDateRow({ d, onBook }) {
  const seats = (d.max_participants || 15) - (d.current_bookings || 0);
  const soldOut = seats <= 0;
  const ext = d.external_booking_url;
  const extOnly = d.booking_type === "external" || !!ext;
  const opens = d.booking_opens_at ? new Date(d.booking_opens_at) : null;
  const locked = !extOnly && opens && !isNaN(opens.getTime()) && opens.getTime() > Date.now();
  return (
    <div id={`termin-${d.id}`} className="plant-card p-4 md:p-5 flex flex-col sm:flex-row sm:items-center gap-3 justify-between scroll-mt-28 target:ring-2 target:ring-[var(--wp-olive)]" data-testid={`tour-date-${d.id}`}>
      <div>
        <div className="font-medium">{formatTourDate(d.date)}</div>
        <div className="text-sm opacity-80 flex items-center gap-1.5 mt-0.5" data-testid={`tour-date-meeting-${d.id}`}>
          <MapPin className="w-4 h-4 shrink-0" style={{ color: "var(--wp-olive)" }} /> Treffpunkt: {d.meeting_point}
        </div>
        {!extOnly && <div className="text-xs opacity-60 mt-0.5">{soldOut ? "Ausgebucht" : `${seats} von ${d.max_participants || 15} Plätzen frei`}</div>}
        <a href={`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/tours/${d.id}/calendar.ics`} className="text-xs underline opacity-70 hover:opacity-100" style={{ color: "var(--wp-olive)" }} data-testid={`tour-date-ics-${d.id}`}>Zum Kalender hinzufügen</a>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <DateShare d={d} />
        {locked && (
          <span className="text-sm px-4 py-2 rounded-full" style={{ backgroundColor: "var(--wp-cream)", color: "var(--wp-olive)", border: "1px dashed var(--wp-olive)" }} data-testid={`tour-date-locked-${d.id}`}>
            Anmeldung ab {formatTourDate(d.booking_opens_at, { weekday: undefined, year: undefined })}
          </span>
        )}
        {!extOnly && !locked && (soldOut ? (
          <span className="text-sm font-semibold px-4 py-2 rounded-full" style={{ backgroundColor: "rgba(167,58,58,0.1)", color: "var(--wp-toxic)" }} data-testid={`tour-date-soldout-${d.id}`}>Ausgebucht</span>
        ) : (
          <button onClick={() => onBook(d)} className="btn-primary justify-center" data-testid={`book-date-${d.id}`}>Anmelden</button>
        ))}
        {ext && (
          <a href={ext} target="_blank" rel="noopener noreferrer" className="btn-primary justify-center" data-testid={`book-date-external-${d.id}`}>
            <ExternalLink className="w-4 h-4" /> Zur Anmeldung
          </a>
        )}
      </div>
    </div>
  );
}
